import { TrendingUp } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { getLevelFromXP, getXPToNextLevel, LEVEL_DEFINITIONS } from '@/types/gamification.types'
import { cn } from '@/lib/utils'

interface LevelProgressProps {
  totalXP: number
  level: number
  variant?: 'full' | 'compact' | 'minimal'
  className?: string
}

export function LevelProgress({
  totalXP,
  level,
  variant = 'full',
  className,
}: LevelProgressProps) {
  const currentLevel = getLevelFromXP(totalXP)
  const xpInfo = getXPToNextLevel(totalXP)
  const progressPercent = xpInfo.required > 0
    ? (xpInfo.current / xpInfo.required) * 100
    : 100

  if (variant === 'minimal') {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <Badge variant="outline" className="font-bold">
          Lv.{level}
        </Badge>
        <span className="text-sm text-muted-foreground">
          {currentLevel.title}
        </span>
      </div>
    )
  }

  if (variant === 'compact') {
    return (
      <div className={cn('flex items-center gap-3', className)}>
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-lg font-bold text-primary">{level}</span>
          </div>
          <div>
            <p className="text-sm font-medium">{currentLevel.title}</p>
            <p className="text-xs text-muted-foreground">
              {totalXP.toLocaleString()} XP
            </p>
          </div>
        </div>
        <div className="flex-1 max-w-[100px]">
          <Progress value={progressPercent} className="h-2" />
        </div>
      </div>
    )
  }

  // Full variant
  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg">
            <span className="text-2xl font-bold text-primary-foreground">{level}</span>
          </div>
          <div>
            <p className="text-lg font-semibold">{currentLevel.title}</p>
            <p className="text-sm text-muted-foreground">
              {totalXP.toLocaleString()} Total XP
            </p>
          </div>
        </div>
        
        {xpInfo.nextLevel && (
          <div className="text-right">
            <p className="text-sm font-medium">Next: {xpInfo.nextLevel.title}</p>
            <p className="text-xs text-muted-foreground">
              {(xpInfo.required - xpInfo.current).toLocaleString()} XP to go
            </p>
          </div>
        )}
      </div>
      
      <div className="space-y-1">
        <Progress value={progressPercent} className="h-3" />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{xpInfo.current.toLocaleString()} XP</span>
          {xpInfo.nextLevel ? (
            <span>{xpInfo.required.toLocaleString()} XP</span>
          ) : (
            <span className="text-success">Max Level!</span>
          )}
        </div>
      </div>
      
      {level < 10 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <TrendingUp className="h-3 w-3" />
          <span>
            {Math.ceil((xpInfo.required - xpInfo.current) / 25)} more achievements to level up
          </span>
        </div>
      )}
    </div>
  )
}

// Export level badge component for use in other places
export function LevelBadge({ level }: { level: number }) {
  const levelInfo = LEVEL_DEFINITIONS.find(l => l.level === level) || LEVEL_DEFINITIONS[0]
  
  return (
    <Badge 
      variant="outline" 
      className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20"
    >
      <span className="font-bold mr-1">Lv.{level}</span>
      <span className="text-muted-foreground">{levelInfo.title}</span>
    </Badge>
  )
}
